<template>
  <div class="etm">
    <!-- Barra: volver / gestionar -->
    <div class="etm__bar">
      <button
        v-if="level !== 0"
        type="button"
        class="etm__back"
        @click="handleBackClick"
      >
        <feather-icon
          icon="ChevronLeftIcon"
          size="18"
        />
        <span>{{ $t('back') }}</span>
      </button>
      <span
        v-else
        class="etm__hint"
      >Selecciona una categoría</span>

      <button
        v-if="allowManage"
        type="button"
        class="etm__manage"
        @click="isEditing = !isEditing"
      >
        <feather-icon
          size="16"
          :icon="isEditing ? 'XIcon' : 'EditIcon'"
        />
        <span>{{ isEditing ? $t('cancel') : $t('edit') }}</span>
      </button>
    </div>

    <!-- Ruta seleccionada -->
    <div
      v-if="pathLabel"
      class="etm__path"
    >
      {{ pathLabel }}
    </div>

    <!-- Categorías -->
    <div class="etm__grid">
      <button
        v-for="category in expensesTypes"
        :key="category.id"
        type="button"
        :class="['etm__item', { 'is-editing': isEditing }]"
        @click="handleExpenseTypeClick(category)"
      >
        <span class="etm__ico">
          <feather-icon
            v-if="isFeatherIcon(category.icon)"
            :icon="category.icon"
            size="22"
          />
          <span
            v-else
            class="etm__emoji"
          >{{ category.icon }}</span>
        </span>
        <span class="etm__name">{{ category.name || '-' }}</span>
        <span
          v-if="hasChildren(category)"
          class="etm__more"
        >
          <feather-icon
            icon="ChevronRightIcon"
            size="12"
          />
        </span>

        <span
          v-if="isEditing"
          class="etm__actions"
        >
          <span
            class="etm__action"
            @click.stop="handleEditClick(category)"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
            />
          </span>
          <span
            class="etm__action etm__action--danger"
            @click.stop="handleDeleteClick(category)"
          >
            <feather-icon
              icon="Trash2Icon"
              size="14"
            />
          </span>
        </span>
      </button>

      <button
        v-b-modal="'addCategoryModal' + index"
        type="button"
        class="etm__item etm__item--add"
      >
        <span class="etm__ico etm__ico--add">
          <feather-icon
            icon="PlusIcon"
            size="22"
          />
        </span>
        <span class="etm__name">Agregar</span>
      </button>
    </div>

    <!-- Categoría final (sin subcategorías) -->
    <p
      v-if="showLeafNotice && level !== 0 && !expensesTypes.length"
      class="etm__leaf"
    >
      <feather-icon
        icon="CheckCircleIcon"
        size="15"
        class="mr-25"
      />
      Listo: se usará <strong>{{ currentCategoryName }}</strong>
    </p>

    <add-category-modal
      :id="'addCategoryModal'+index"
      :project-id="$route.params.projectId"
      :parent-id="record.expense_type_catalog_id"
      @refetch="$emit('refetch')"
      @update="getSubExpenseType"
    />
    <edit-category-modal
      v-if="expenseTypeToEdit"
      :id="editModalId"
      :key="expenseTypeToEdit.id"
      :project-id="$route.params.projectId"
      :parent-id="record.expense_type_catalog_id"
      :category="expenseTypeToEdit"
      @update="getSubExpenseType"
      @refetch="$emit('refetch')"
    />
    <confirmation-modal
      :id="deleteModalId"
      :variant="'danger'"
      @confirm="removeExpenseType()"
    >
      <template v-slot:content>
        {{ $t('deleteExpenseTypeConfirmation', {
          expenseType: expenseTypeToDelete ? expenseTypeToDelete.name : ''
        }) }}
      </template>
    </confirmation-modal>
  </div>

</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import AddCategoryModal from './AddCategoryModal.vue'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import EditCategoryModal from './EditCategoryModal.vue'

export default {
  components: {
    AddCategoryModal,
    ConfirmationModal,
    FeatherIcon,
    EditCategoryModal,
  },
  props: {
    index: {
      type: Number,
      default: () => (0),
    },
    showLeafNotice: {
      type: Boolean,
      default: true,
    },
    record: {
      type: Object,
      default: () => ({}),
    },
    expenseTypeCatalogs: {
      type: Array,
      default: () => ([]),
    },
    history: {
      type: Array,
      default: () => ([]),
    },
    allowManage: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isEditing: false,
      expenseTypeToDelete: null,
      expenseTypeToEdit: null,
    }
  },
  computed: {
    ...mapGetters('groupedExpenses', [
      'subExpenseType',
    ]),
    expensesTypes() {
      const source = this.record.expense_type_catalog_id
        ? (this.history[this.level - 1] || {}).subcategories
        : this.expenseTypeCatalogs

      return (Array.isArray(source) ? source : []).map(expense => ({
        name: expense.name,
        id: expense.id,
        icon: expense && expense.icon ? expense.icon : '🏷️',
        subcategories: expense.subcategories || [],
      }))
    },
    level() {
      return this.history.length
    },
    editModalId() {
      return `editCategoryModal${this.index}`
    },
    deleteModalId() {
      return `confirmDelete${this.index}`
    },
    currentCategoryName() {
      return (this.history[this.level - 1] || {}).name || ''
    },
    pathLabel() {
      return this.history.map(item => item && item.name).filter(Boolean).join(' › ')
    },
  },
  methods: {
    ...mapActions('groupedExpenses', [
      'deleteExpensesTypesCatalog',
      'fetchSubExpenseTypeCatalog',
    ]),
    // Los íconos guardados son nombres de vue-feather-icons (HomeIcon, TruckIcon…).
    // Si el valor no cumple ese formato lo tratamos como emoji y lo pintamos como texto.
    isFeatherIcon(value) {
      return /^[A-Z][A-Za-z0-9]*Icon$/.test(String(value || ''))
    },
    hasChildren(category) {
      return Array.isArray(category.subcategories) && category.subcategories.length > 0
    },
    handleBackClick() {
      this.history.pop()
      const lastCat = this.history[this.level - 1] || null
      this.record.expense_type_catalog_id = lastCat?.id || null
      this.$emit('setExpenseType', lastCat?.name || '')
    },
    handleExpenseTypeClick(category) {
      if (this.isEditing) return
      this.record.expense_type_catalog_id = category.id
      this.$emit('setExpenseType', category.name)
      this.getSubExpenseType()
    },
    handleDeleteClick(category) {
      this.expenseTypeToDelete = category
      this.$bvModal.show(this.deleteModalId)
    },
    async handleEditClick(category) {
      this.expenseTypeToEdit = category
      await this.$nextTick()
      this.$bvModal.show(this.editModalId)
    },
    removeExpenseType() {
      this.deleteExpensesTypesCatalog({ projectId: this.$route.params.projectId, expenseTypeId: this.expenseTypeToDelete.id }).then(() => {
        this.$emit('refetch')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('expenseTypeDeleted'),
            icon: 'SendIcon',
            variant: 'success',
          },
        })
      })
    },
    getSubExpenseType() {
      this.fetchSubExpenseTypeCatalog({ projectId: this.$route.params.projectId, expenseTypeId: this.record.expense_type_catalog_id }).then(data => {
        this.history.push(data)
      })
    },
  },
}
</script>
<style lang="scss" scoped>
$etm-primary: #340668;
$etm-line: #eceaf1;
$etm-muted: #6b7280;

.etm__bar {
  align-items: center;
  border-bottom: 1px solid $etm-line;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  min-height: 44px;
  padding-bottom: 0.5rem;
}

.etm__back,
.etm__manage {
  align-items: center;
  background: transparent;
  border: 0;
  color: $etm-primary;
  display: inline-flex;
  font-size: 0.85rem;
  font-weight: 600;
  gap: 0.25rem;
  padding: 0.35rem 0;
}

.etm__hint {
  color: $etm-muted;
  font-size: 0.85rem;
}

.etm__path {
  color: $etm-muted;
  font-size: 0.78rem;
  overflow-wrap: anywhere;
  padding: 0.5rem 0 0.25rem;
}

/* Cuadrícula: se adapta al ancho, no se rompe con nombres largos */
.etm__grid {
  display: grid;
  gap: 0.35rem;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  padding: 0.5rem 0;
}

.etm__item {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-height: 104px;
  padding: 0.65rem 0.25rem;
  position: relative;
  text-align: center;

  &:active {
    background: #f5f4f8;
  }

  &.is-editing {
    animation: etm-vibrate 0.2s linear infinite;
  }
}

.etm__ico {
  align-items: center;
  background: #f7f5fc;
  border: 1px solid #e6e0f4;
  border-radius: 50%;
  color: $etm-primary;
  display: flex;
  flex: 0 0 auto;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.etm__emoji {
  font-size: 24px;
  line-height: 1;
}

.etm__ico--add {
  background: #fff;
  border: 1px dashed #c8bfe0;
}

.etm__name {
  color: #111827;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.etm__more {
  color: $etm-muted;
  position: absolute;
  right: 0.35rem;
  top: 0.65rem;
}

.etm__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.15rem;
}

.etm__action {
  align-items: center;
  background: #f5f4f8;
  border-radius: 6px;
  color: $etm-primary;
  display: inline-flex;
  height: 26px;
  justify-content: center;
  width: 26px;

  &--danger {
    color: #b42318;
  }
}

.etm__leaf {
  align-items: center;
  background: #e8f8ef;
  border-radius: 10px;
  color: #067647;
  display: flex;
  font-size: 0.85rem;
  margin: 0.25rem 0 0;
  padding: 0.6rem 0.75rem;
}

@keyframes etm-vibrate {
  0% { transform: translate(0); }
  20% { transform: translate(-1px, 1px); }
  40% { transform: translate(1px, -1px); }
  60% { transform: translate(-1px, -1px); }
  80% { transform: translate(1px, 1px); }
  100% { transform: translate(0); }
}
</style>
